import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import CircularProgress from "@material-ui/core/CircularProgress";
import CallReceivedIcon from "@material-ui/icons/CallReceived";
import CallMadeIcon from "@material-ui/icons/CallMade";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import GetAppIcon from "@material-ui/icons/GetApp";
import clsx from "clsx";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { formatPhone } from "../../utils/formatPhone";
import { useSortable } from "../../utils/useSortable";

const BODY_FONT = "Poppins, sans-serif";
const GREEN = "#86C240"; // acento verde do estado ativo (pills/filtros), igual às abas

// botões rápidos de período — presets que setam De/Até (filtro server-side)
const p2 = n => String(n).padStart(2, "0");
const toYMD = d => `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
const fmtDMY = ymd => {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  return `${d}/${m}/${y}`;
};
const PRESETS = [
  { key: "today", label: "Hoje" },
  { key: "yesterday", label: "Ontem" },
  { key: "thisMonth", label: "Este mês" },
  { key: "lastMonth", label: "Mês passado" },
  { key: "last30", label: "Últimos 30 dias" },
  { key: "last90", label: "Últimos 90 dias" },
];
const presetRange = key => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const shift = (base, days) => { const x = new Date(base); x.setDate(x.getDate() + days); return x; };
  switch (key) {
    case "today": return [today, today];
    case "yesterday": { const y = shift(today, -1); return [y, y]; }
    case "thisMonth": return [new Date(now.getFullYear(), now.getMonth(), 1), today];
    case "lastMonth": return [new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 0)];
    case "last30": return [shift(today, -29), today];
    case "last90": return [shift(today, -89), today];
    default: return [null, null];
  }
};

// extrai o valor ordenável de cada coluna do CDR
const cdrVal = (c, key) => {
  switch (key) {
    case "when": return c.attemptedAt ? new Date(c.attemptedAt).getTime() : 0;
    case "direction": return c.direction || "";
    case "from": return c.from || "";
    case "to": return c.to || "";
    case "duration": return c.durationSec == null ? -1 : c.durationSec;
    case "status": return c.sipStatus || 0;
    default: return "";
  }
};

const useStyles = makeStyles(theme => ({
  page: { height: "100%", overflowY: "auto", background: theme.app.chatBg },
  wrap: { width: "100%", maxWidth: 1200, margin: "0 auto", padding: theme.spacing(3.5, 3, 6) },
  pageHead: { marginBottom: theme.spacing(2) },
  title: { fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, fontSize: 22, color: theme.app.ink },
  titleCount: { fontFamily: BODY_FONT, fontSize: 12, color: theme.app.textTertiary, marginLeft: 8 },
  grow: { flexGrow: 1 },
  numLink: { cursor: "pointer", "&:hover": { color: theme.app.info, textDecoration: "underline" } },

  // painel de filtros (cinza claro, campos brancos) — estilo ref
  filterBar: { background: "#EDEEF1", borderRadius: 16, padding: theme.spacing(2), marginBottom: theme.spacing(2.5), fontFamily: BODY_FONT },
  dateRow: { display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap", marginBottom: 12 },
  date: { width: 168, "& .MuiOutlinedInput-root": { background: "#fff", borderRadius: 8 } },
  clearBtn: { textTransform: "none", fontSize: 12.5, fontWeight: 500, color: theme.app.textSecondary, fontFamily: BODY_FONT },
  showing: { marginLeft: "auto", alignSelf: "center", fontSize: 12, color: theme.app.textTertiary },

  // botões rápidos (presets de período) + filtro por direção — pills, ativo verde
  presets: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12 },
  searchFull: { width: "100%", marginBottom: 12 },
  searchInput: { borderRadius: 999, background: "#fff", fontFamily: BODY_FONT },
  dirPills: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  pill: {
    display: "inline-flex", alignItems: "center", gap: 5, height: 30, padding: "0 14px",
    borderRadius: 999, border: `1px solid ${theme.app.border}`, background: "#fff",
    color: theme.app.textSecondary, fontSize: 12.5, fontWeight: 600, cursor: "pointer",
    userSelect: "none", fontFamily: BODY_FONT,
    "&:hover": { background: theme.app.hover },
  },
  pillOn: { background: GREEN, borderColor: GREEN, color: "#fff", "&:hover": { background: GREEN } },
  pillCount: { fontSize: 11, fontWeight: 500, opacity: 0.85 },
  tbl: { background: "#fff", border: `1px solid ${theme.app.border}`, borderRadius: 10, overflow: "hidden" },
  headCell: {
    fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: theme.app.textTertiary,
    fontWeight: 600, borderBottom: `1px solid ${theme.app.border}`, padding: "0 16px", height: 38, lineHeight: "38px",
  },
  row: { "&:hover": { backgroundColor: theme.app.hover } },
  cell: { borderBottom: `1px solid ${theme.app.lineSoft}`, padding: "0 16px", height: 46, fontSize: 13, color: theme.app.ink },
  num: { fontFamily: theme.app.mono, fontSize: 12.5, color: theme.app.ink },
  when: { color: theme.app.textSecondary, fontSize: 12.5, fontVariantNumeric: "tabular-nums" },
  dir: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 500, padding: "2px 9px", borderRadius: 20 },
  dirIn: { color: theme.app.info, background: "#EAF1F8" },
  dirOut: { color: theme.app.ok || "#1E9E62", background: "#E7F4EC" },
  dirIcon: { fontSize: 14 },
  dur: { fontFamily: theme.app.mono, fontSize: 12.5, color: theme.app.textSecondary, fontVariantNumeric: "tabular-nums" },
  badge: { fontSize: 11.5, fontWeight: 500, padding: "2px 9px", borderRadius: 5, display: "inline-block" },
  ok: { color: theme.app.ok || "#1E9E62", background: "#E7F4EC" },
  warn: { color: theme.app.warn || "#C77A12", background: "#FBF1E2" },
  bad: { color: theme.app.danger || "#C0453B", background: "#F8E9E8" },
  muted: { color: theme.app.textTertiary },
  empty: { padding: theme.spacing(6, 2), textAlign: "center", color: theme.app.textTertiary, fontSize: 13 },
  center: { display: "flex", justifyContent: "center", padding: 24 },
  more: { display: "flex", justifyContent: "center", marginTop: 14 },
  moreBtn: { textTransform: "none", fontSize: 12.5, color: theme.app.textSecondary },
}));

const pad2 = n => String(n).padStart(2, "0");
const fmtDur = s => {
  if (s == null) return "—";
  const m = Math.floor(s / 60);
  return `${m}:${pad2(s % 60)}`;
};
const fmtWhen = iso => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

// disposição amigável a partir do sip_status / termination_reason
const disposition = c => {
  if (c.answered || c.sipStatus === 200) return { label: "Concluída", cls: "ok" };
  if (c.sipStatus === 486) return { label: "Ocupado", cls: "warn" };
  if (c.sipStatus === 487) return { label: "Cancelada", cls: "warn" };
  if (c.sipStatus === 480 || c.sipStatus === 408) return { label: "Não atendida", cls: "warn" };
  if (c.sipStatus === 404 || c.sipStatus === 484) return { label: "Número inválido", cls: "bad" };
  if (c.sipStatus && c.sipStatus >= 400) return { label: c.terminationReason || `Falha (${c.sipStatus})`, cls: "bad" };
  return { label: c.terminationReason || "—", cls: "muted" };
};

const Cdrs = ({ embedded = false } = {}) => {
  const classes = useStyles();
  const [cdrs, setCdrs] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dir, setDir] = useState("all"); // all | inbound | outbound
  const [search, setSearch] = useState("");
  const [start, setStart] = useState(""); // YYYY-MM-DD — filtro por período (server-side)
  const [end, setEnd] = useState("");
  const [preset, setPreset] = useState(""); // botão rápido de período ativo

  const applyPreset = key => {
    const [s, e] = presetRange(key);
    setStart(toYMD(s));
    setEnd(toYMD(e));
    setPreset(key);
  };

  const fetchPage = useCallback(async (p, append) => {
    setLoading(true);
    try {
      const params = { page: p, count: 50 };
      if (start) params.start = start;
      if (end) params.end = end;
      const { data } = await api.get("/cdrs", { params });
      const rows = data.cdrs || [];
      setCdrs(prev => (append ? [...prev, ...rows] : rows));
      setTotal(data.total || rows.length);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  // recarrega da 1ª página na carga e sempre que o período muda
  useEffect(() => {
    setPage(1);
    fetchPage(1, false);
  }, [fetchPage]);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchPage(next, true);
  };

  // baixa o pcap (SIP trace) da chamada — proxy no backend com a chave da conta
  const downloadPcap = async c => {
    try {
      const { data } = await api.get(`/cdrs/${c.callSid}/pcap`, {
        params: { sipCallId: c.sipCallId },
        responseType: "blob",
      });
      // pcap só com header (24 bytes) = sem pacotes capturados pra essa chamada
      if (!data || data.size <= 24) {
        toast.info("Sem captura de pacotes para esta chamada.");
        return;
      }
      const url = window.URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `callid-${c.sipCallId}.pcap`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toastError(err);
    }
  };

  // filtros (client-side, sobre o que já carregou): direção + busca por número
  const q = search.replace(/\D/g, "");
  const filtered = cdrs.filter(c => {
    if (dir !== "all" && c.direction !== dir) return false;
    if (q && !(`${c.from || ""}${c.to || ""}`.replace(/\D/g, "").includes(q))) return false;
    return true;
  });
  // contagem por direção (sobre a busca atual) para os pills de filtro
  const bySearch = cdrs.filter(c => !q || `${c.from || ""}${c.to || ""}`.replace(/\D/g, "").includes(q));
  const dirCount = v => (v === "all" ? bySearch.length : bySearch.filter(c => c.direction === v).length);
  const { sorted, sort, toggle } = useSortable(filtered, cdrVal);

  const th = (key, label) => (
    <TableCell className={classes.headCell} sortDirection={sort.key === key ? sort.dir : false}>
      <TableSortLabel active={sort.key === key} direction={sort.key === key ? sort.dir : "asc"} onClick={() => toggle(key)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const content = (
    <>
        {!embedded && (
          <div className={classes.pageHead}>
            <Typography className={classes.title}>
              Histórico de chamadas<span className={classes.titleCount}>{total}</span>
            </Typography>
          </div>
        )}

        <div className={classes.filterBar}>
          <div className={classes.dateRow}>
            <TextField
              className={classes.date}
              type="date"
              label="De"
              variant="outlined"
              size="small"
              value={start}
              onChange={e => { setStart(e.target.value); setPreset(""); }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: end || undefined }}
            />
            <TextField
              className={classes.date}
              type="date"
              label="Até"
              variant="outlined"
              size="small"
              value={end}
              onChange={e => { setEnd(e.target.value); setPreset(""); }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: start || undefined }}
            />
            {(start || end) && (
              <Button className={classes.clearBtn} onClick={() => { setStart(""); setEnd(""); setPreset(""); }}>
                Limpar
              </Button>
            )}
            {(start || end) && (
              <span className={classes.showing}>Exibindo {fmtDMY(start)} – {fmtDMY(end)}</span>
            )}
          </div>

          <div className={classes.presets}>
            {PRESETS.map(p => (
              <button
                key={p.key}
                type="button"
                className={clsx(classes.pill, preset === p.key && classes.pillOn)}
                onClick={() => applyPreset(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>

          <TextField
            className={classes.searchFull}
            placeholder="Buscar por número (origem ou destino)…"
            variant="outlined"
            size="small"
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              className: classes.searchInput,
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            }}
          />

          <div className={classes.dirPills}>
            {[["all", "Todas"], ["inbound", "Recebidas"], ["outbound", "Efetuadas"]].map(([v, lab]) => (
              <button
                key={v}
                type="button"
                className={clsx(classes.pill, dir === v && classes.pillOn)}
                onClick={() => setDir(v)}
              >
                {lab}
                <span className={classes.pillCount}>({dirCount(v)})</span>
              </button>
            ))}
          </div>
        </div>

        <div className={classes.tbl}>
          <Table>
            <TableHead>
              <TableRow>
                {th("when", "Quando")}
                {th("direction", "Direção")}
                {th("from", "De")}
                {th("to", "Para")}
                {th("duration", "Duração")}
                {th("status", "Status")}
                <TableCell className={classes.headCell} />
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((c, i) => {
                const inbound = c.direction === "inbound";
                const disp = disposition(c);
                return (
                  <TableRow key={c.callSid || i} className={classes.row}>
                    <TableCell className={clsx(classes.cell, classes.when)}>{fmtWhen(c.attemptedAt)}</TableCell>
                    <TableCell className={classes.cell}>
                      <span className={clsx(classes.dir, inbound ? classes.dirIn : classes.dirOut)}>
                        {inbound ? <CallReceivedIcon className={classes.dirIcon} /> : <CallMadeIcon className={classes.dirIcon} />}
                        {inbound ? "Entrada" : "Saída"}
                      </span>
                    </TableCell>
                    <TableCell className={clsx(classes.cell, classes.num)}>
                      {c.from ? (
                        <span className={classes.numLink} title="Filtrar por este número" onClick={() => setSearch(c.from)}>
                          {formatPhone(c.from)}
                        </span>
                      ) : "—"}
                    </TableCell>
                    <TableCell className={clsx(classes.cell, classes.num)}>
                      {c.to ? (
                        <span className={classes.numLink} title="Filtrar por este número" onClick={() => setSearch(c.to)}>
                          {formatPhone(c.to)}
                        </span>
                      ) : "—"}
                    </TableCell>
                    <TableCell className={clsx(classes.cell, classes.dur)}>{fmtDur(c.durationSec)}</TableCell>
                    <TableCell className={classes.cell}>
                      <span className={clsx(classes.badge, classes[disp.cls])}>{disp.label}</span>
                    </TableCell>
                    <TableCell className={classes.cell} align="right">
                      {c.sipCallId && (
                        <Tooltip title="Baixar pcap (SIP trace)">
                          <IconButton size="small" onClick={() => downloadPcap(c)}>
                            <GetAppIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {loading && cdrs.length === 0 && (
            <div className={classes.center}><CircularProgress size={24} /></div>
          )}
          {!loading && cdrs.length === 0 && (
            <div className={classes.empty}>Nenhuma chamada ainda.</div>
          )}
          {!loading && cdrs.length > 0 && sorted.length === 0 && (
            <div className={classes.empty}>Nenhuma chamada com esse filtro.</div>
          )}
        </div>

        {cdrs.length > 0 && cdrs.length < total && (
          <div className={classes.more}>
            <Button className={classes.moreBtn} onClick={loadMore} disabled={loading}>
              {loading ? "Carregando…" : "Carregar mais"}
            </Button>
          </div>
        )}
    </>
  );

  // embedded: usado como aba dentro de Números (sem o wrapper de página/título)
  if (embedded) return content;
  return (
    <div className={classes.page}>
      <div className={classes.wrap}>{content}</div>
    </div>
  );
};

export default Cdrs;
